import React, { useEffect, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';
import { useNation } from '../i18n/NationContext';

/**
 * Segmented-tab nation switcher — big, unmissable, keyboard-friendly.
 *  - Two side-by-side buttons ("England & Wales" / "Scotland").
 *  - Active nation gets a filled purple pill; other is a soft outline.
 *  - Clicking a tab filters the page content (via NationContext)
 *    AND scrolls the viewport to the top so the new content is visible.
 *  - `role="tablist"` + `aria-selected` for accessible switching.
 *  - First-visit pulse: three gentle purple rings play once, then never
 *    again for that browser. Anonymous — a `localStorage` flag stops it.
 *    Global `prefers-reduced-motion: reduce` rule in index.css skips it.
 */
const OPTIONS = [
  { id: 'ew', label: 'England & Wales', short: 'E&W' },
  { id: 'scotland', label: 'Scotland', short: 'Scotland' },
];
const PULSE_SEEN_KEY = 'tfi_picker_pulse_seen_v1';

export default function NationPicker({ testid = 'nation-picker', compact = false }) {
  const { nation, setNation } = useNation();
  const [pulsing, setPulsing] = useState(false);
  const pulseTimer = useRef(null);

  useEffect(() => {
    try {
      const seen = window.localStorage.getItem(PULSE_SEEN_KEY);
      if (seen) return; // already pulsed for this browser
    } catch {
      /* private-mode storage error — just skip the pulse */
      return;
    }
    // Small delay so the picker has painted before the pulse starts.
    const start = window.setTimeout(() => setPulsing(true), 450);
    // 3 pulses × 1.6s each ≈ 4.8s; mark seen just after so it never replays.
    pulseTimer.current = window.setTimeout(() => {
      setPulsing(false);
      try {
        window.localStorage.setItem(PULSE_SEEN_KEY, '1');
      } catch {
        /* ignore */
      }
    }, 5300);
    return () => {
      window.clearTimeout(start);
      if (pulseTimer.current) window.clearTimeout(pulseTimer.current);
    };
  }, []);

  const stopPulse = () => {
    if (!pulsing) return;
    setPulsing(false);
    try {
      window.localStorage.setItem(PULSE_SEEN_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  const handleChoose = (id) => {
    stopPulse();
    setNation(id);
  };

  return (
    <section
      className="tf-no-print mt-6"
      data-testid={testid}
      aria-label="Choose which nation's law and support to read"
    >
      {!compact && (
        <div className="flex items-center gap-1.5 mb-2">
          <MapPin size={13} color="var(--tf-purple-dark)" aria-hidden="true" />
          <p
            className="font-body font-bold"
            style={{
              fontSize: 11,
              letterSpacing: '0.22em',
              color: 'var(--tf-purple-dark)',
            }}
          >
            READING FOR
          </p>
        </div>
      )}
      <div
        role="tablist"
        aria-label="Nation"
        className={`flex w-full p-1.5 rounded-2xl${pulsing ? ' tf-nation-picker-pulse' : ''}`}
        onMouseEnter={stopPulse}
        onFocus={stopPulse}
        style={{
          background: '#fff',
          border: '2px solid var(--tf-purple-light)',
          boxShadow: '0 4px 14px rgba(35, 20, 70, 0.06)',
          maxWidth: 520,
        }}
      >
        {OPTIONS.map((o) => {
          const active = nation === o.id;
          return (
            <button
              key={o.id}
              type="button"
              role="tab"
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              onClick={() => handleChoose(o.id)}
              data-testid={`${testid}-${o.id}`}
              className="font-body font-bold transition-colors flex-1"
              style={{
                padding: '14px 18px',
                borderRadius: 12,
                fontSize: 15,
                cursor: 'pointer',
                background: active ? 'var(--tf-purple)' : 'transparent',
                color: active ? '#fff' : 'var(--tf-purple-dark)',
                border: 'none',
                minHeight: 52,
                boxShadow: active ? '0 6px 16px rgba(139, 123, 177, 0.28)' : 'none',
                letterSpacing: '-0.005em',
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
      {!compact && (
        <p
          className="font-body mt-2.5"
          style={{
            fontSize: 12,
            color: 'var(--tf-text-muted)',
            lineHeight: 1.5,
            maxWidth: '38em',
          }}
        >
          The law is different across the UK. Tap the nation that fits your
          situation — we&apos;ll show the right content and remember your choice
          on other pages. Northern Ireland: follow England &amp; Wales guidance
          for now.
        </p>
      )}
    </section>
  );
}
